import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import "./index.scss";
import {
  formatDate,
  formatNumberWithIntl,
  getFormattedDate,
} from "@wavvest/shared-utils";
import { Collapse, Spin, Alert, Button } from "antd";
import {
  DownloadOutlined,
  EditOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { GoalIcon } from "@/common/widgets/portfolio/icon";
import { ConfidenceChart } from "@/common/components/charts/confidence-chart";
import { AllocationList } from "@/common/widgets/portfolio/allocation-widgets";
import IncomeExpenseChart from "@/common/components/charts/IncomeExpenseProjectionChart";
import {
  getInvestorFinancialPlans,
  // FinancialPlansListData,
  InvestorProfile,
} from "@/api/custom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import HomePriceChart from "@/common/components/charts/home-price-chart";
import {
  GoalType,
  Portfolio,
  PortfolioState,
  IncomeExpenseProjection,
  ProjectedSaving,
  HomePriceProjection,
  entries,
} from "@/common/widgets/portfolio/util";
import FinancialPlanEdit from "./edit-plan/index";

interface FinancialPlanProps {
  user: InvestorProfile;
}

const FinancialPlan: React.FC<FinancialPlanProps> = ({ user }) => {
  const [investorFinancialPlans, setInvestorFinancialPlans] = useState<
    PortfolioState[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState<string | string[]>([]);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PortfolioState | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const isFetchingRef = useRef(false);

  const [indicators, setIndicators] = useState<{
    incomeExpense: {
      x: number;
      y: number;
      name: string;
      income: number;
      expenses: number;
      netSavings: number;
    } | null;
    confidence: {
      x: number;
      y: number;
      value: number;
      name: string;
    } | null;
    homePrice: {
      x: number;
      y: number;
      value: number;
      name: string;
    } | null;
  }>({
    incomeExpense: null,
    confidence: null,
    homePrice: null,
  });

  const fetchProfileData = useCallback(async () => {
    // avoid concurrent requests
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      if ("id" in user) {
        const cognitoId = user.cognito_id?.trim();
        if (!cognitoId) {
          setError("Investor has no cognito_id");
          return;
        }

        const plans = await getInvestorFinancialPlans(cognitoId);
        setInvestorFinancialPlans(plans);
        setHasLoaded(true);
      }
    } catch {
      setError("Failed to load profile data");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    // load data only when not loaded
    // note: when returning from edit mode, handleBack will set hasLoaded to false, triggering a reload
    if (!hasLoaded) {
      fetchProfileData();
    }
    // remove fetchProfileData dependency to avoid loop triggering
  }, [hasLoaded]);

  const YEAR_INTERVAL = 5;

  // const getColor = useCallback((progress: number) => {
  //   const colorPlan = [
  //     { min: 0, max: 0.4, color: "#FF725E" },
  //     { min: 0.4, max: 0.7, color: "#979797" },
  //     { min: 0.7, max: 1, color: "#56C34D" },
  //   ];
  //   return (
  //     colorPlan.find(
  //       ({ min, max }) => progress > min && progress <= max
  //     )?.color || "#000"
  //   );
  // }, []);

  const downloadPdf = useCallback(
    (index: number) => {
      const element = document.getElementById(
        `investorFinancialPlanIndex${index}`,
      );
      if (!element) return;

      const pdf = new jsPDF("p", "mm", "a4");
      const userName = user?.name || "Unknown User";
      const userEmail = user?.email || "Unknown Email";

      let planName = "Unknown Plan";
      const timestamp = investorFinancialPlans[index]?.scenarioTimestamp;
      if (timestamp && typeof timestamp === "string") {
        try {
          planName = formatDate(timestamp) || "Unknown Plan";
        } catch (error) {
          console.error("Error formatting date:", error);
          planName = "Unknown Plan";
        }
      }

      pdf.setFontSize(12);
      pdf.text(`Name: ${userName}`, 10, 10);
      pdf.text(`Email: ${userEmail}`, 10, 20);
      pdf.text(`Plan: ${planName}`, 10, 30);

      html2canvas(element, { scrollY: -window.scrollY }).then((canvas) => {
        const imgWidth = 210; // A4 page width is 210mm
        const pageHeight = 297; // A4 page height is 297mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 40;
        const imgData = canvas.toDataURL("image/png");

        while (heightLeft > 0) {
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
          position -= pageHeight;
          if (heightLeft > 0) pdf.addPage();
        }
        pdf.save(`FinancialPlan_${getFormattedDate()}.pdf`);
      });
    },
    [user, investorFinancialPlans],
  );

  const genExtra = useCallback(
    (index: number, plan: PortfolioState) =>
      activeKey.includes(index.toString()) && (
        <div className="collapse-item-handle-button">
          {index === 0 && (
            <EditOutlined
              className="handle-icon"
              onClick={(event) => {
                event.stopPropagation();
                setSelectedPlan(plan);
                setShowEdit(true);
              }}
            />
          )}
          <DownloadOutlined
            className="handle-icon"
            onClick={(event) => {
              event.stopPropagation();
              downloadPdf(index);
            }}
          />
        </div>
      ),
    [activeKey, downloadPdf],
  );

  const collapseItems = useMemo(() => {
    function formatDataByYearGap(
      data: ProjectedSaving[] | HomePriceProjection[],
      gapYear: number,
    ): ProjectedSaving[] | HomePriceProjection[] {
      if (!Array.isArray(data) || data.length === 0) return data;
      return data.filter((_, index) => index % gapYear === 0) as
        | ProjectedSaving[]
        | HomePriceProjection[];
    }

    return investorFinancialPlans.map(
      (
        investorFinancialPlan: PortfolioState,
        investorFinancialPlanIndex: number,
      ) => {
        const enabledPortfolios = entries(
          investorFinancialPlan.portfolios,
        ).filter(
          ([, portfolio]) =>
            portfolio !== undefined && portfolio.timeHorizon > 0,
        ) as [GoalType, Portfolio][];

        const filteredProjection =
          investorFinancialPlan.incomeExpenseProjection?.filter(
            (item) => item.year % YEAR_INTERVAL === 0,
          );

        const projectedSavingData = formatDataByYearGap(
          investorFinancialPlan.projectionSavings || [],
          YEAR_INTERVAL,
        );

        const homePriceProjectionData = formatDataByYearGap(
          investorFinancialPlan.homePriceProjection || [],
          YEAR_INTERVAL,
        );

        return {
          key: investorFinancialPlanIndex.toString(),
          label:
            investorFinancialPlanIndex === 0
              ? "Current Plan"
              : `Past Plan : ${formatDate(investorFinancialPlan.scenarioTimestamp as string)}`,
          children: (
            <div
              className="investorFinancialPlan"
              id={`investorFinancialPlanIndex${investorFinancialPlanIndex}`}
            >
              <div className="summarize">
                <div className="financialgoals summarize-item">
                  <div className="name">
                    {investorFinancialPlanIndex === 0 ? "Current" : "Past"}{" "}
                    Plan: Financial Goals
                  </div>
                  <div className="value">
                    {enabledPortfolios.map(
                      (item: [GoalType, Portfolio], idx: number) => {
                        return (
                          <div className="goal" key={`allocation${idx}`}>
                            <GoalIcon goal={item[0]} />
                            <span className="goal-name">{item[0]}</span>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
                <div className="probability summarize-item">
                  <div className="name">Probability of success</div>
                  <div className="value">
                    {investorFinancialPlan.probabilityOfSuccess
                      ? `${(investorFinancialPlan.probabilityOfSuccess as number).toFixed(2)}%`
                      : "0"}
                  </div>
                </div>
                <div className="total-income summarize-item">
                  <div className="name">Total income</div>
                  <div className="value">
                    {formatNumberWithIntl({
                      num:
                        (investorFinancialPlan.userInfo
                          ?.annualIncome as number) || 0,
                    })}
                  </div>
                </div>
              </div>
              <div className="cuerrent">
                {filteredProjection && filteredProjection.length > 0 && (
                  <IncomeExpenseChart
                    data={filteredProjection as IncomeExpenseProjection[]}
                    themeColors={{
                      backgroundColor: "#222222",
                      textColor: "#ABABAB",
                      incomeColor: "#5359D8",
                      expenseColor: "#724189",
                      netSavingsColor: "#62B4BF",
                    }}
                    indicator={indicators.incomeExpense}
                    setIndicator={(val) =>
                      setIndicators((prev) => ({ ...prev, incomeExpense: val }))
                    }
                  />
                )}
                {projectedSavingData?.length > 0 && (
                  <ConfidenceChart
                    data={projectedSavingData as ProjectedSaving[]}
                    themeColors={{
                      backgroundColor: "#222222",
                      textColor: "#ABABAB",
                      lineColor: "#AD56D6",
                      areaColors: {
                        confidence95: "#BDA1C9",
                        confidence75: "#A078AA",
                      },
                    }}
                    indicator={indicators.confidence}
                    setIndicator={(val) =>
                      setIndicators((prev) => ({ ...prev, confidence: val }))
                    }
                  />
                )}
                {homePriceProjectionData?.length > 0 && (
                  <HomePriceChart
                    data={homePriceProjectionData as HomePriceProjection[]}
                    themeColors={{
                      backgroundColor: "#222222",
                      textColor: "#ABABAB",
                      lineColor: "#AD56D6",
                      areaColors: { start: "#724189", end: "#D9D9D900" },
                    }}
                    indicator={indicators.homePrice}
                    setIndicator={(val) =>
                      setIndicators((prev) => ({ ...prev, homePrice: val }))
                    }
                  />
                )}

                {enabledPortfolios.map(
                  (item: [GoalType, Portfolio], idx: number) => {
                    return (
                      <div className="chart" key={`allocation${idx}`}>
                        <div className="title">
                          <GoalIcon goal={item[0]} />
                          <span className="title-name">{item[0]}</span>
                        </div>
                        <AllocationList goal={item[0]} portfolio={item[1]} />
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          ),
          showArrow: false,
          style: {
            borderRadius: 12,
            textAlign: "left",
            background:
              investorFinancialPlanIndex === 0 ? "#DCCDE2" : "#F0F0F0",
          },
          extra: genExtra(investorFinancialPlanIndex, investorFinancialPlan),
        };
      },
    );
  }, [investorFinancialPlans, genExtra, indicators]);

  const latestItem = collapseItems[0];
  const pastItems = collapseItems.slice(1);

  const onCollapseChange = (key: string | string[]) => {
    setActiveKey(Array.isArray(key) ? key : [key]);
  };

  const handleBack = () => {
    setShowEdit(false);
    setSelectedPlan(null);
    setHasLoaded(false);
  };

  if (showEdit && selectedPlan) {
    return (
      <div className="financial-plan-edit">
        <div className="back-button">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            style={{ marginBottom: 16 }}
          />
        </div>
        <FinancialPlanEdit
          plan={selectedPlan}
          user={user}
          onBack={handleBack}
        />
      </div>
    );
  }

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  return (
    <div className="financial-plan">
      <Spin spinning={loading} tip="Loading data...">
        <div className="content">
          {latestItem && (
            <Collapse
              items={[latestItem]}
              activeKey={activeKey}
              onChange={onCollapseChange}
              size="large"
              className="current plan-collapse"
            />
          )}
          {pastItems.length > 0 && (
            <Collapse
              items={pastItems}
              activeKey={activeKey}
              onChange={onCollapseChange}
              size="large"
              className="past plan-collapse"
            />
          )}
        </div>
      </Spin>
    </div>
  );
};

export default FinancialPlan;
